// src/components/alerts/AlertDropdown.jsx
import React, { useEffect, useState, useRef } from "react";
import api from "../../api/axios";
import AlertItem from "./AlertItem";
import AlertToastItem from "./AlertToastItem";
import toast from "react-hot-toast";
import "./AlertDropdown.css";

const PAGE_SIZE = 10;

const AlertDropdown = ({ isOpen, setOpen, setUnreadCount, buttonRef }) => {
  const [alerts, setAlerts] = useState([]);
  const [visibleAlerts, setVisibleAlerts] = useState([]);
  const [page, setPage] = useState(1);

  const dropdownRef = useRef(null);
  const scrollRef = useRef(null);

  // 🔔 toast 중복 방지용
  const prevAlertIdsRef = useRef(new Set());
  const pollingRef = useRef(null);

  /**
   * 🔔 알림 권한 요청 (최초 1회)
   */
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // 3초 후 권한 요청 (사용자 경험 개선)
      setTimeout(() => {
        Notification.requestPermission().then(permission => {
          console.log('알림 권한:', permission);
        });
      }, 3000);
    }
  }, []);

  /**
   * 🔄 5초 polling
   * - 최초 1회는 기준만 세팅
   * - 이후 새로 들어온 읽지 않은 알림만 toast
   */
  const pollNewAlerts = async () => {
    try {
      const res = await api.get("alerts/");
      const list = res.data.alerts || [];

      // 최초 기준 설정
      if (prevAlertIdsRef.current.size === 0) {
        prevAlertIdsRef.current = new Set(list.map((a) => a.id));
        setAlerts(list);
        setUnreadCount(list.filter((a) => !a.is_read).length);
        return;
      }

      const prevIds = prevAlertIdsRef.current;

      const newAlerts = list.filter(
        (a) => !prevIds.has(a.id) && !a.is_read && !isOpen
      );

      newAlerts.forEach((alert) => {
        // 1. Toast 알림 (기존)
        toast.custom(
          (t) => (
            <div
              className={`
                pointer-events-auto
                max-w-[380px]
                rounded-lg
                shadow-xl
                ${alert.is_urgent ? 'toast-urgent-gradient' : 'toast-normal-gradient'}
                ${t.visible ? "toast-enter" : "toast-leave"}
                cursor-pointer
                hover:shadow-2xl
                transition-shadow
              `}
              onClick={() => toast.dismiss(t.id)}
            >
              <AlertToastItem alert={alert} />
            </div>
          ),
          {
            duration: alert.is_urgent ? 6000 : 3000,
          }
        );

        // 2. 브라우저 알림 (탭이 백그라운드일 때)
        if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
          const doc = alert.document;
          const notificationTitle = alert.is_urgent ? '[긴급 알림]' : '[새 알림]';
          const notificationBody = alert.message;

          // 문서 URL 생성
          const targetUrl = doc
            ? `/project/${doc.project}/${doc.module}/${doc.doc_type}/${doc.dns}/view`
            : alert.project
              ? `/project/${alert.project}/docs/data`
              : '/';

          // Service Worker를 통한 알림 표시
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'SHOW_NOTIFICATION',
              title: notificationTitle,
              options: {
                body: notificationBody,
                icon: '/mobile_chatbot.png',
                badge: '/logo192.ico',
                vibrate: alert.is_urgent ? [200, 100, 200, 100, 200] : [200],
                tag: `alert-${alert.id}`,
                requireInteraction: alert.is_urgent,
                data: { url: targetUrl }
              }
            });
          } else {
            // Fallback: Service Worker 없으면 일반 알림
            const notification = new Notification(notificationTitle, {
              body: notificationBody,
              icon: '/mobile_chatbot.png',
              vibrate: alert.is_urgent ? [200, 100, 200, 100, 200] : [200],
              tag: `alert-${alert.id}`,
            });

            notification.onclick = () => {
              window.focus();
              window.location.href = targetUrl;
              notification.close();
            };
          }
        }
      });

      // 기준 갱신
      prevAlertIdsRef.current = new Set(list.map((a) => a.id));

      setAlerts(list);
      setUnreadCount(list.filter((a) => !a.is_read).length);
    } catch (e) {
      console.error("alert polling error", e);
    }
  };

  /**
   * 🔁 polling 시작 (1개만)
   */
  useEffect(() => {
    pollNewAlerts();
    pollingRef.current = setInterval(pollNewAlerts, 10000);

    return () => clearInterval(pollingRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * 📥 드롭다운 열릴 때 최신 알림 로드
   */
  const loadLatestAlerts = async () => {
    const res = await api.get("alerts/");
    const list = res.data.alerts || [];

    setAlerts(list);
    setVisibleAlerts(list.slice(0, PAGE_SIZE));
    setPage(1);
    setUnreadCount(list.filter((a) => !a.is_read).length);
  };

  useEffect(() => {
    if (isOpen) {
      loadLatestAlerts();
    }
  }, [isOpen]);

  /**
   * 📩 읽음 처리
   */
  const handleRead = async (id) => {
    await api.post(`alerts/${id}/read/`);

    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, is_read: true } : a))
    );

    setUnreadCount((prev) => Math.max(prev - 1, 0));
    setOpen(false);
  };

  /**
   * 🗑️ 삭제 처리
   */
  const handleDelete = async (id) => {
    try {
      await api.delete(`alerts/${id}/delete/`);

      setAlerts((prev) => prev.filter((a) => a.id !== id));
      setVisibleAlerts((prev) => prev.filter((a) => a.id !== id));
      setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * 📜 무한 스크롤
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isLoadingTop = false;

    const handleScroll = async () => {
      // ⬇️ 아래
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
        const nextPage = page + 1;
        const nextItems = alerts.slice(0, nextPage * PAGE_SIZE);

        if (nextItems.length > visibleAlerts.length) {
          setVisibleAlerts(nextItems);
          setPage(nextPage);
        }
      }

      // ⬆️ 위 (최신 갱신)
      if (el.scrollTop === 0 && !isLoadingTop) {
        isLoadingTop = true;
        await loadLatestAlerts();
        setTimeout(() => {
          isLoadingTop = false;
        }, 300);
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [alerts, page, visibleAlerts]);

  /**
   * 🖱️ 바깥 클릭 시 닫기 (버튼 제외)
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      // 드롭다운 외부 클릭 && 버튼 클릭 아닐 때만 닫기
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setOpen, buttonRef]);

  return (
    <div
      ref={dropdownRef}
      className={`
        absolute right-0 mt-2 w-[400px] max-h-[520px]
        rounded-xl bg-white shadow-2xl z-50 border border-gray-100
        ${!isOpen ? "hidden" : "dropdown-fade-in"}
      `}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">알림</h3>
          {visibleAlerts.length > 0 && (
            <span className="text-xs text-gray-500">
              {alerts.filter((a) => !a.is_read).length}개의 새 알림
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div ref={scrollRef} className="max-h-[450px] overflow-y-auto custom-scrollbar">
        {visibleAlerts.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">알림이 없습니다</p>
            <p className="text-xs text-gray-500">새로운 알림이 도착하면 여기에 표시됩니다</p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-100">
              {visibleAlerts.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  onRead={handleRead}
                  onDelete={handleDelete}
                />
              ))}
            </ul>

            {visibleAlerts.length >= alerts.length && (
              <div className="select-none py-4 text-center text-[11px] text-gray-400 bg-gray-50">
                <p className="font-medium">추가로 표시할 알림이 없습니다</p>
                <p className="mt-1">알림은 최대 7일간 보관됩니다</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AlertDropdown;
